<template>
  <section class="dp-layout screen-device-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1" />
    </section>
    <section class="dp-right">
      <div class="content-wrapper config-panel">
        <div class="header-compact">
          <div class="header-row header-row--title">
            <div class="header-title-group">
              <span class="page-title">大屏管理员权限</span>
              <el-tooltip
                content="按学校为管理员配置可管理设备 SN 与可发布地址；切换管理员后自动加载其历史权限。"
                placement="bottom"
                effect="dark"
              >
                <i class="el-icon-info hint-icon" aria-label="说明"></i>
              </el-tooltip>
            </div>
            <div class="header-actions-inline">
              <template v-if="currentPath">
                <span class="school-label">当前学校</span>
                <el-tag size="small" type="info" effect="plain" class="school-tag">{{ schoolName }}</el-tag>
              </template>
              <el-button
                size="small"
                type="warning"
                plain
                :disabled="!adminUserId"
                @click="clearPermission"
              >清空权限</el-button>
              <el-button
                type="success"
                size="small"
                :disabled="!adminUserId"
                @click="savePermission"
              >保存权限</el-button>
            </div>
          </div>
          <div class="header-row header-row--admin">
            <el-form :inline="true" size="small" class="header-form header-form--tight">
              <el-form-item label="管理员" class="form-item-admin">
                <el-select
                  v-model="adminUserId"
                  filterable
                  clearable
                  :loading="teacherLoading"
                  placeholder="请选择管理员"
                  class="admin-select"
                  @change="handleAdminUserChange"
                >
                  <el-option
                    v-for="item in teacherOptions"
                    :key="item.Id"
                    :label="`${item.TeacherName || ''}${item.Number ? `（${item.Number}）` : ''}`"
                    :value="item.Id"
                  />
                </el-select>
              </el-form-item>
            </el-form>
          </div>
        </div>

        <el-alert
          v-if="schoolId && !adminUserId"
          class="admin-pick-hint"
          type="info"
          :closable="false"
          show-icon
        >
          请先在上方选择<strong>管理员</strong>，再勾选设备与发布地址；未选择管理员时无法保存，下方列表仅可查看。
        </el-alert>

        <div class="card-grid--outer">
          <div v-if="!schoolId" class="empty-holder">
            <el-empty description="请选择左侧学校后配置管理员权限"></el-empty>
          </div>
          <template v-else>
          <div
            class="permission-body-wrap"
            :class="{ 'permission-body-wrap--loading': adminPermissionLoading && adminUserId }"
            v-loading="adminPermissionLoading && adminUserId"
            element-loading-text="正在加载权限配置..."
            element-loading-spinner="el-icon-loading"
          >
          <div v-if="adminUserId" class="permission-summary-card">
            <div class="summary-compare">
              <div class="summary-side summary-side--saved">
                <div class="summary-side-label">已保存</div>
                <div class="summary-chips">
                  <el-tag size="small" type="info" effect="plain" class="num-chip">设备 {{ savedSnCount }}</el-tag>
                  <el-tag size="small" type="info" effect="plain" class="num-chip">地址 {{ savedUrlCount }}</el-tag>
                </div>
              </div>
              <div class="summary-vs" aria-hidden="true">
                <i class="el-icon-d-arrow-right"></i>
              </div>
              <div class="summary-side summary-side--draft" :class="{ 'summary-side--dirty': isPermissionDirty }">
                <div class="summary-side-head">
                  <span class="summary-side-label">本次勾选</span>
                  <el-tag v-if="isPermissionDirty" type="warning" size="mini" effect="dark" class="pending-tag">待保存</el-tag>
                  <el-tag v-else type="success" size="mini" effect="plain" class="sync-tag">已同步</el-tag>
                </div>
                <div class="summary-chips">
                  <el-tag
                    size="small"
                    :type="chipTypeForCount(currentSnCount, savedSnCount)"
                    effect="plain"
                    class="num-chip"
                  >设备 {{ currentSnCount }}</el-tag>
                  <el-tag
                    size="small"
                    :type="chipTypeForCount(currentUrlCount, savedUrlCount)"
                    effect="plain"
                    class="num-chip"
                  >地址 {{ currentUrlCount }}</el-tag>
                </div>
              </div>
            </div>
            <!-- 固定占位，避免「变动说明」显隐时整页布局跳动 -->
            <div
              v-if="isPermissionDirty"
              class="summary-delta-slot summary-delta-slot--fill"
            >
              <div class="summary-delta-line">
                {{ permissionDirtySummary }}
              </div>
            </div>
          </div>
          <div class="card-grid--inner">
          <el-card shadow="hover" class="screen-card permission-card">
            <div slot="header" class="card-header">
              <div class="title-with-icon">
                <i class="el-icon-cpu main-icon" style="color: #2563eb"></i>
                <span class="screen-name">可管理 SN 列表</span>
              </div>
            </div>
            <div class="card-content" v-loading="snLoading">
              <div
                class="sn-pick-shell compact-panel sn-panel"
                :class="{ 'pick-panel--no-admin': !adminUserId }"
              >
                <div class="sn-pick-scroll">
                  <div class="option-list sn-option-list">
                    <div
                      v-for="item in snOptions"
                      :key="item.Sn"
                      class="option-row clickable"
                      :class="{ active: isSnSelected(item.Sn) }"
                      @click="toggleSn(item.Sn)"
                    >
                      <div class="option-row-main">
                        <span class="option-title">{{ item.Sn }}</span>
                      </div>
                      <div class="option-sub" v-if="item.DeviceName">{{ item.DeviceName }}</div>
                    </div>
                  </div>
                </div>
                <div v-if="!snLoading && snOptions.length === 0" class="sn-empty-wrap">
                  <el-empty description="暂无可选设备" :image-size="56" />
                </div>
              </div>
            </div>
          </el-card>

          <el-card shadow="hover" class="screen-card permission-card">
            <div slot="header" class="card-header">
              <div class="title-with-icon">
                <i class="el-icon-monitor main-icon" style="color: #14B8A6"></i>
                <span class="screen-name">可发布地址列表</span>
              </div>
            </div>
            <div class="card-content" v-loading="urlLoading">
              <div
                class="url-pick-shell compact-panel"
                :class="{ 'pick-panel--no-admin': !adminUserId }"
              >
                <div class="url-pick-scroll">
                <div class="group-title">常规地址</div>
                <div v-for="group in generalUrlGroups" :key="group.label" class="sub-group">
                  <div class="sub-group-title">{{ group.label }}</div>
                  <div class="option-list">
                    <div
                      v-for="item in group.options"
                      :key="item.value"
                      class="option-row clickable"
                      :class="{ active: isGeneralUrlSelected(item.value) }"
                      @click="toggleGeneralUrl(item.value)"
                    >
                      <div class="option-row-main">
                        <div class="screen-url-option">
                          <i :class="item.icon" :style="{ color: item.color || '#909399' }"></i>
                          <span class="option-title">{{ item.label.split(' - ')[0] }}</span>
                        </div>
                        <el-button type="text" size="mini" @click.stop="openUrl(item.value)">预览</el-button>
                      </div>
                      <div class="option-sub">{{ item.value }}</div>
                    </div>
                  </div>
                </div>

                <div class="group-title" style="margin-top: 10px;">作业公示</div>
                <div class="homework-search-wrap">
                  <el-input
                    v-model.trim="homeworkKeyword"
                    size="small"
                    clearable
                    placeholder="搜索班级名称"
                    :disabled="!adminUserId"
                  />
                </div>
                <div class="option-list homework-list">
                  <div
                    v-for="item in pagedHomeworkClassOptions"
                    :key="item.value"
                    class="option-row clickable"
                    :class="{ active: isHomeworkSelected(item.value) }"
                    @click="toggleHomework(item.value)"
                  >
                    <div class="option-row-main">
                      <div class="screen-url-option">
                        <i class="el-icon-reading" style="color: #14B8A6"></i>
                        <span class="option-title">{{ item.label }}</span>
                      </div>
                      <el-button type="text" size="mini" @click.stop="openUrl(`${baseUrl}/HomeWorkPage?id=${item.value}`)">预览</el-button>
                    </div>
                    <div class="option-sub">{{ baseUrl }}/HomeWorkPage?id={{ item.value }}</div>
                  </div>
                </div>
                <el-empty v-if="!urlLoading && filteredHomeworkClassOptions.length === 0" description="暂无作业公示班级" :image-size="56" />
                </div>
                <!-- 分页固定在面板底部，不随列表滚动 -->
                <div class="homework-pagination-wrap" v-if="filteredHomeworkClassOptions.length > homeworkPageSize">
                  <el-pagination
                    layout="total, sizes, prev, pager, next, jumper"
                    :total="filteredHomeworkClassOptions.length"
                    :page-size="homeworkPageSize"
                    :page-sizes="[5, 10, 20, 50]"
                    :current-page.sync="homeworkCurrentPage"
                    @size-change="handleHomeworkPageSizeChange"
                    small
                  />
                </div>
              </div>
            </div>
          </el-card>
          </div>
          </div>
          </template>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import { getScreenAdminPermission, saveScreenAdminPermission } from '../../api/modules/system';
import { getTeacherMasterList } from '../../api/modules/personnel';
import { getScreenDeviceOptions } from '../../api/modules/integration';
import { getSubDepartmentTree } from '../../api/modules/department';
import { getScreenList, parseScreenListResponse } from "@/api/dismissal";
import DepartmentPane from "@/components/DepartmentPane.vue";

export default {
  name: "ScreenDeviceManageAdminPermission",
  components: { DepartmentPane },
  data() {
    return {
      schoolId: 0,
      schoolName: "",
      adminUserId: null,
      teacherOptions: [],
      teacherLoading: false,
      snOptions: [],
      snLoading: false,
      generalUrlGroups: [],
      homeworkClassOptions: [],
      urlLoading: false,
      baseUrl: window.location.origin,
      staticScreens: [
        { name: "考勤大屏", path: "/AttenBigScreen", param: "departmentId", icon: "el-icon-time", color: "#2563eb" },
        { name: "宿舍大屏", path: "/static/dorm-screen/index.html", param: "departmentId", icon: "el-icon-house", color: "#67C23A" },
        { name: "请假大屏", path: "/LeaveBigScreen", param: "departmentId", icon: "el-icon-suitcase", color: "#E6A23C" },
        { name: "离校大屏", path: "/LeaveOutBigScreen", param: "departmentId", icon: "el-icon-circle-close", color: "#F56C6C" },
        { name: "学校大屏", path: "/SchoolInfoScreen", param: "departmentId", icon: "el-icon-school", color: "#909399" },
        { name: "德育大屏", path: "/MoralEducationBigScreen", param: "departmentId", icon: "el-icon-medal", color: "#00bcd4" },
        { name: "积分大屏", path: "/MoralPointBigScreen", param: "departmentId", icon: "el-icon-coin", color: "#ffb347" },
        { name: "哲商五育大屏", path: "/ZheshangFiveEducationScreen", param: "departmentId", icon: "el-icon-data-analysis", color: "#7c3aed" },
        { name: "哲商学生画像", path: "/ZheshangStudentPortraitScreen", param: "departmentId", icon: "el-icon-user", color: "#ec4899" }
      ],
      permission: {
        SnList: [],
        AllowedScreenUrlList: []
      },
      selectedGeneralUrls: [],
      selectedHomeworkClassIds: [],
      homeworkKeyword: "",
      homeworkPageSize: 5,
      homeworkCurrentPage: 1,
      /** 最近一次从服务端加载或保存成功后的权限快照，用于对比「变动」 */
      permissionSnapshot: {
        SnList: [],
        AllowedScreenUrlList: []
      },
      /** 切换管理员并拉取权限时为 true，避免快照与勾选短暂不一致导致界面误闪 */
      adminPermissionLoading: false
    };
  },
  computed: {
    currentPath() {
      return this.schoolId;
    },
    /** 当前勾选即将保存的地址列表（与保存逻辑一致） */
    builtAllowedUrlList() {
      const homeworkUrls = this.buildHomeworkUrls();
      return [...(this.selectedGeneralUrls || []), ...homeworkUrls]
        .filter((x) => !!x)
        .filter((x, idx, arr) => arr.indexOf(x) === idx);
    },
    currentSnCount() {
      return (this.permission.SnList || []).length;
    },
    currentUrlCount() {
      return this.builtAllowedUrlList.length;
    },
    savedSnCount() {
      return (this.permissionSnapshot.SnList || []).length;
    },
    savedUrlCount() {
      return (this.permissionSnapshot.AllowedScreenUrlList || []).length;
    },
    isPermissionDirty() {
      if (!this.adminUserId || !this.schoolId) {
        return false;
      }
      return !this.sameStringArray(this.permission.SnList, this.permissionSnapshot.SnList)
        || !this.sameStringArray(this.builtAllowedUrlList, this.permissionSnapshot.AllowedScreenUrlList);
    },
    permissionDirtySummary() {
      const dSn = this.currentSnCount - this.savedSnCount;
      const dUrl = this.currentUrlCount - this.savedUrlCount;
      const partSn =
        dSn === 0
          ? `设备 ${this.currentSnCount} 台（与已保存一致）`
          : `设备 ${this.savedSnCount} → ${this.currentSnCount} 台（${dSn > 0 ? "+" : ""}${dSn}）`;
      const partUrl =
        dUrl === 0
          ? `地址 ${this.currentUrlCount} 个（与已保存一致）`
          : `地址 ${this.savedUrlCount} → ${this.currentUrlCount} 个（${dUrl > 0 ? "+" : ""}${dUrl}）`;
      return `${partSn}；${partUrl}`;
    },
    filteredHomeworkClassOptions() {
      const keyword = (this.homeworkKeyword || "").toLowerCase();
      if (!keyword) {
        return this.homeworkClassOptions || [];
      }
      return (this.homeworkClassOptions || []).filter((x) => String(x.label || "").toLowerCase().includes(keyword));
    },
    pagedHomeworkClassOptions() {
      const start = (this.homeworkCurrentPage - 1) * this.homeworkPageSize;
      const end = start + this.homeworkPageSize;
      return this.filteredHomeworkClassOptions.slice(start, end);
    }
  },
  watch: {
    homeworkKeyword() {
      this.homeworkCurrentPage = 1;
    }
  },
  methods: {
    isSnSelected(sn) {
      return (this.permission.SnList || []).includes(sn);
    },
    isGeneralUrlSelected(url) {
      return (this.selectedGeneralUrls || []).includes(url);
    },
    isHomeworkSelected(classId) {
      return (this.selectedHomeworkClassIds || []).includes(classId);
    },
    toggleInArray(list, value) {
      const arr = Array.isArray(list) ? [...list] : [];
      const idx = arr.indexOf(value);
      if (idx >= 0) {
        arr.splice(idx, 1);
      } else {
        arr.push(value);
      }
      return arr;
    },
    toggleSn(sn) {
      if (!this.adminUserId) {
        return;
      }
      this.permission.SnList = this.toggleInArray(this.permission.SnList, sn);
    },
    toggleGeneralUrl(url) {
      if (!this.adminUserId) {
        return;
      }
      this.selectedGeneralUrls = this.toggleInArray(this.selectedGeneralUrls, url);
    },
    toggleHomework(classId) {
      if (!this.adminUserId) {
        return;
      }
      this.selectedHomeworkClassIds = this.toggleInArray(this.selectedHomeworkClassIds, classId);
    },
    /** 作业公示分页条数变更。最后修改：2026-08-28 */
    handleHomeworkPageSizeChange(pageSize) {
      this.homeworkPageSize = pageSize;
      this.homeworkCurrentPage = 1;
    },
    openUrl(url) {
      if (!this.adminUserId) {
        return;
      }
      if (!url) {
        return;
      }
      window.open(url, "_blank");
    },
    /** 本次与已保存数量不一致时用警告色标签突出 */
    chipTypeForCount(current, saved) {
      return Number(current) !== Number(saved) ? "warning" : "success";
    },
    treeNodeClick(node) {
      this.schoolId = Number(node && node.value ? node.value : 0);
      this.schoolName = node && node.label ? node.label : "";
      this.adminUserId = null;
      this.resetPermissionSelections();
      this.snOptions = [];
      this.generalUrlGroups = [];
      this.homeworkClassOptions = [];
      this.homeworkCurrentPage = 1;
      this.homeworkKeyword = "";
      this.loadTeacherOptions();
      this.loadSnOptions();
      this.loadScreenUrlOptions();
    },
    async handleAdminUserChange(value) {
      if (!value) {
        this.resetPermissionSelections();
        this.adminPermissionLoading = false;
        return;
      }
      this.adminPermissionLoading = true;
      // 同步清空勾选与快照，避免出现「已保存为 0、本次仍为上一管理员勾选」的误闪
      this.resetPermissionSelections();
      try {
        const loaded = await this.loadPermission();
        if (loaded) {
          const snCount = (this.permission.SnList || []).length;
          const urlCount = (this.permission.AllowedScreenUrlList || []).length;
          const currentTeacher = (this.teacherOptions || []).find((x) => x.Id === value);
          const teacherName = currentTeacher && currentTeacher.TeacherName ? currentTeacher.TeacherName : "当前管理员";
          this.$message({
            type: "success",
            message: `已加载${teacherName}权限：SN ${snCount} 个，地址 ${urlCount} 个`,
            duration: 1500,
            showClose: false
          });
        }
      } finally {
        this.adminPermissionLoading = false;
      }
    },
    resetPermissionSelections() {
      this.permission.SnList = [];
      this.permission.AllowedScreenUrlList = [];
      this.selectedGeneralUrls = [];
      this.selectedHomeworkClassIds = [];
      this.homeworkKeyword = "";
      this.permissionSnapshot = { SnList: [], AllowedScreenUrlList: [] };
    },
    /** 将当前界面选择固化为「已保存」对比基线 */
    capturePermissionSnapshot() {
      this.permissionSnapshot = {
        SnList: [...(this.permission.SnList || [])],
        AllowedScreenUrlList: [...this.builtAllowedUrlList]
      };
    },
    /** 比较两个字符串数组是否一致（忽略顺序） */
    sameStringArray(a, b) {
      const norm = (arr) =>
        [...(arr || [])]
          .map((x) => String(x).trim())
          .filter(Boolean)
          .sort((x, y) => x.localeCompare(y, "zh-CN"));
      const as = norm(a);
      const bs = norm(b);
      if (as.length !== bs.length) {
        return false;
      }
      return as.every((v, i) => v === bs[i]);
    },
    async loadTeacherOptions() {
      if (!this.schoolId) {
        this.teacherOptions = [];
        return;
      }
      this.teacherLoading = true;
      try {
        const res = await getTeacherMasterList({
          page: 1,
          intPageSize: 9999,
          DepartmentId: this.schoolId
        });
        if (res.data && res.data.response) {
          this.teacherOptions = res.data.response || [];
        } else {
          this.teacherOptions = [];
        }
      } finally {
        this.teacherLoading = false;
      }
    },
    async loadSnOptions() {
      if (!this.schoolId) {
        this.snOptions = [];
        return;
      }
      this.snLoading = true;
      try {
        const res = await getScreenDeviceOptions({ schoolId: this.schoolId });
        if (res.data && res.data.success) {
          this.snOptions = res.data.response || [];
        } else {
          this.snOptions = [];
        }
      } finally {
        this.snLoading = false;
      }
    },
    async loadScreenUrlOptions() {
      if (!this.schoolId) {
        this.generalUrlGroups = [];
        this.homeworkClassOptions = [];
        this.selectedGeneralUrls = [];
        this.selectedHomeworkClassIds = [];
        return;
      }
      this.urlLoading = true;
      try {
        const baseOptions = [];
        this.staticScreens.forEach((item) => {
          const url = `${this.baseUrl}${item.path}?${item.param}=${this.schoolId}`;
          baseOptions.push({
            label: `${item.name} - ${url}`,
            value: url,
            icon: item.icon,
            color: item.color
          });
        });

        const [dismissalRes, classesRes] = await Promise.all([
          getScreenList({ schId: this.schoolId, page: 1, intPageSize: 999 }),
          getSubDepartmentTree({ pid: this.schoolId })
        ]);

        const dismissalOptions = [];
        if (dismissalRes.data.success) {
          const dismissalRows = parseScreenListResponse(dismissalRes).slice().sort((a, b) => {
            return String(a.ScreenName || "").localeCompare(String(b.ScreenName || ""), "zh-CN");
          });
          dismissalRows.forEach((item) => {
            const url = `${this.baseUrl}/DismissalBigScreen?code=${item.ScreenCode}`;
            dismissalOptions.push({
              label: `放学大屏-${item.ScreenName} - ${url}`,
              value: url,
              icon: "el-icon-s-data",
              color: "#9B59B6"
            });
          });
        }

        const homeworkOptions = [];
        if (classesRes.data.success) {
          const classOptions = this.extractClasses(classesRes.data.response).sort((a, b) => {
            return String(a.label || "").localeCompare(String(b.label || ""), "zh-CN");
          });
          classOptions.forEach((item) => {
            const url = `${this.baseUrl}/HomeWorkPage?id=${item.value}`;
            homeworkOptions.push({
              label: `作业公示-${item.label} - ${url}`,
              value: url,
              icon: "el-icon-reading",
              color: "#14B8A6"
            });
          });
          this.homeworkClassOptions = classOptions;
        } else {
          this.homeworkClassOptions = [];
        }

        this.generalUrlGroups = [
          { label: "基础大屏", options: baseOptions },
          { label: "放学大屏", options: dismissalOptions }
        ].filter((x) => x.options && x.options.length);
        this.syncSelectionsFromPermission(homeworkOptions);
      } finally {
        this.urlLoading = false;
      }
    },
    extractClasses(nodes) {
      const results = [];
      const traverse = (list) => {
        if (!list) {
          return;
        }
        const items = Array.isArray(list) ? list : [list];
        items.forEach((node) => {
          if (node.DeptOrgType === "class") {
            results.push({ label: node.label, value: node.value });
          }
          if (node.children && node.children.length) {
            traverse(node.children);
          }
        });
      };
      traverse(nodes);
      return results;
    },
    async loadPermission() {
      if (!this.schoolId) {
        this.resetPermissionSelections();
        return false;
      }
      if (!this.adminUserId) {
        this.resetPermissionSelections();
        return false;
      }
      const res = await getScreenAdminPermission({ adminUserId: this.adminUserId, schoolId: this.schoolId });
      if (res.data.success) {
        const data = res.data.response || {};
        this.permission.SnList = data.SnList || [];
        this.permission.AllowedScreenUrlList = data.AllowedScreenUrlList || [];
        this.syncSelectionsFromPermission();
        this.$nextTick(() => {
          this.capturePermissionSnapshot();
        });
        return true;
      } else {
        this.$message.error(res.data.msg || "查询失败");
        this.resetPermissionSelections();
        return false;
      }
    },
    syncSelectionsFromPermission(homeworkUrlOptionsInput) {
      const allUrls = this.permission.AllowedScreenUrlList || [];
      const generalUrlMap = {};
      (this.generalUrlGroups || []).forEach((group) => {
        (group.options || []).forEach((item) => {
          generalUrlMap[item.value] = true;
        });
      });

      const homeworkUrlOptions = homeworkUrlOptionsInput || this.buildHomeworkUrlOptions();
      const homeworkUrlMap = {};
      homeworkUrlOptions.forEach((item) => {
        homeworkUrlMap[item.value] = item.classId;
      });

      this.selectedGeneralUrls = allUrls.filter((url) => generalUrlMap[url]);
      this.selectedHomeworkClassIds = allUrls
        .filter((url) => homeworkUrlMap[url])
        .map((url) => homeworkUrlMap[url]);
    },
    buildHomeworkUrlOptions() {
      return (this.homeworkClassOptions || []).map((item) => ({
        classId: item.value,
        value: `${this.baseUrl}/HomeWorkPage?id=${item.value}`
      }));
    },
    buildHomeworkUrls() {
      return (this.selectedHomeworkClassIds || []).map((classId) => `${this.baseUrl}/HomeWorkPage?id=${classId}`);
    },
    async clearPermission() {
      if (!this.schoolId) {
        this.$message.warning("请先在左侧选择学校");
        return;
      }
      if (!this.adminUserId) {
        this.$message.warning("请先选择管理员用户");
        return;
      }
      try {
        await this.$confirm("确认清空当前管理员的全部权限吗？", "清空确认", {
          confirmButtonText: "确认清空",
          cancelButtonText: "取消",
          type: "warning"
        });
      } catch (e) {
        return;
      }

      this.permission.SnList = [];
      this.selectedGeneralUrls = [];
      this.selectedHomeworkClassIds = [];
      this.permission.AllowedScreenUrlList = [];
      await this.savePermission();
    },
    getSelectedAddressNames() {
      const nameMap = {};
      (this.generalUrlGroups || []).forEach((group) => {
        (group.options || []).forEach((item) => {
          nameMap[item.value] = item.label ? item.label.split(" - ")[0] : item.value;
        });
      });
      const homeworkMap = {};
      (this.homeworkClassOptions || []).forEach((item) => {
        homeworkMap[item.value] = `作业公示-${item.label}`;
      });

      const generalNames = (this.selectedGeneralUrls || []).map((url) => nameMap[url] || url);
      const homeworkNames = (this.selectedHomeworkClassIds || []).map((classId) => homeworkMap[classId] || `作业公示-${classId}`);
      return [...generalNames, ...homeworkNames];
    },
    async savePermission() {
      const homeworkUrls = this.buildHomeworkUrls();
      const allowedScreenUrlList = [...(this.selectedGeneralUrls || []), ...homeworkUrls]
        .filter((x) => !!x)
        .filter((x, idx, arr) => arr.indexOf(x) === idx);
      this.permission.AllowedScreenUrlList = allowedScreenUrlList;
      const payload = {
        schoolId: this.schoolId,
        adminUserId: this.adminUserId,
        SnList: this.permission.SnList || [],
        AllowedScreenUrlList: allowedScreenUrlList
      };
      if (!this.schoolId) {
        this.$message.warning("请先在左侧选择学校");
        return;
      }
      if (!this.adminUserId) {
        this.$message.warning("请先选择管理员用户");
        return;
      }
      const snCount = (payload.SnList || []).length;
      const urlCount = (payload.AllowedScreenUrlList || []).length;
      const isClearAction = snCount === 0 && urlCount === 0;
      const snPreview = (payload.SnList || []).slice(0, 6);
      const addressNames = this.getSelectedAddressNames();
      const addressPreview = addressNames.slice(0, 8);
      const snMoreText = snCount > snPreview.length ? ` 等${snCount}个` : "";
      const addressMoreText = addressNames.length > addressPreview.length ? ` 等${addressNames.length}个` : "";
      const detailLines = [
        isClearAction ? "确认清空当前管理员权限配置吗？" : "确认保存当前管理员权限配置吗？",
        `设备 ${snCount} 个，地址 ${urlCount} 个`,
        `设备号：${snPreview.length ? snPreview.join("、") + snMoreText : "无"}`,
        `地址：${addressPreview.length ? addressPreview.join("、") + addressMoreText : "无"}`
      ];
      try {
        await this.$confirm(detailLines.join("<br/>"), "保存确认", {
          confirmButtonText: isClearAction ? "确认清空" : "确认保存",
          cancelButtonText: "取消",
          type: "warning",
          dangerouslyUseHTMLString: true
        });
      } catch (e) {
        return;
      }
      const res = await saveScreenAdminPermission(payload);
      if (res.data.success) {
        this.$message.success(res.data.msg || "保存成功");
        this.$nextTick(() => {
          this.capturePermissionSnapshot();
        });
      } else {
        this.$message.error(res.data.msg || "保存失败");
      }
    }
  }
};
</script>

<style lang="stylus" scoped>
.screen-device-page.dp-layout
  height 100%
  box-sizing border-box
  overflow hidden
  @media (max-width: 1366px)
    flex 1
    min-height 0
    flex-direction column
    align-items stretch
    padding 16px
    .dp-left
      width 100% !important
      max-width 100%
      margin-right 0 !important
      margin-bottom 16px
      max-height 280px
      flex-shrink 0
    .dp-right
      width 100%
      min-width 0 !important
      flex 1
      min-height 0
.screen-device-page .dp-right
  display flex
  flex-direction column
  min-width 0
  min-height 0
  overflow hidden
.screen-device-page .dp-right > .content-wrapper.config-panel
  display flex
  flex-direction column
  gap 0
  flex 1
  min-height 0
  overflow hidden
  width 100%
  max-width 100%
  box-sizing border-box
.header-compact
  flex-shrink 0
  margin-bottom 10px
  padding-bottom 8px
  border-bottom 1px solid #ebeef5
.header-row
  display flex
  align-items center
  flex-wrap wrap
  gap 8px
  &--title
    justify-content space-between
    margin-bottom 6px
  &--admin
    margin-bottom 8px
.header-title-group
  display flex
  align-items center
  gap 6px
  min-width 0
.page-title
  font-size 15px
  font-weight 700
  color #303133
  letter-spacing 0.02em
.hint-icon
  font-size 15px
  color #909399
  cursor help
  &:hover
    color #2563eb
.header-actions-inline
  display flex
  align-items center
  gap 10px
  flex-shrink 0
  flex-wrap wrap
  max-width 100%
  /* 仅使用 gap；避免 Element 按钮相邻 margin 与 gap 叠加导致「清空/保存」与左侧间距不一致 */
  >>> .el-button
    margin-left 0
    margin-right 0
  >>> .el-tag
    margin-left 0
    margin-right 0
.school-label
  font-size 12px
  color #909399
.school-tag
  max-width 160px
  overflow hidden
  text-overflow ellipsis
  vertical-align middle
.admin-pick-hint
  flex-shrink 0
  margin-bottom 10px
  >>> .el-alert__content
    line-height 1.5
.header-form--tight
  width 100%
  >>> .el-form-item
    margin-bottom 0
  >>> .el-form-item__label
    padding-right 8px
    color #606266
.form-item-admin
  margin-right 0
.admin-select
  width 280px
  max-width 100%
.permission-summary-card
  flex-shrink 0
  display flex
  flex-direction column
  background #fafcff
  border 1px solid #e4e7ed
  border-radius 8px
  padding 8px 12px
  box-shadow 0 1px 2px rgba(15, 23, 42, 0.04)
.summary-compare
  display flex
  align-items stretch
  flex-wrap wrap
  gap 8px 12px
.summary-side
  flex 1
  min-width 160px
  &--saved
    .summary-side-label
      color #606266
  &--draft
    .summary-side-label
      color #606266
  &--dirty .summary-side-label
    color #606266
.summary-side-label
  font-size 12px
  font-weight 600
  margin-bottom 6px
  display block
.summary-side-head
  display flex
  align-items center
  flex-wrap wrap
  gap 6px
  margin-bottom 6px
  .summary-side-label
    margin-bottom 0
.pending-tag
  font-weight 600
.sync-tag
  border-color #c8e6c9
.summary-chips
  display flex
  flex-wrap wrap
  align-items center
  gap 6px
.num-chip
  font-variant-numeric tabular-nums
.summary-vs
  display flex
  align-items center
  justify-content center
  color #c0c4cc
  font-size 18px
  padding 0 4px
  flex-shrink 0
  align-self center
@media (max-width 640px)
  .summary-compare
    flex-direction column
  .summary-vs
    transform rotate(90deg)
    padding 4px 0
.summary-delta-slot
  flex-shrink 0
  width 100%
  box-sizing border-box
  margin-top 6px
  padding-top 6px
  border-top 1px dashed #e4e7ed
  &--fill
    padding-bottom 2px
.summary-delta-line
  display block
  width 100%
  max-width 100%
  box-sizing border-box
  margin 0
  padding 0
  border none
  font-size 12px
  color #856404
  line-height 1.4
  text-align left
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
.card-grid--outer
  margin-top 4px
  flex 1
  min-height 0
  display flex
  flex-direction column
  overflow hidden
.permission-body-wrap
  position relative
  flex 1
  min-height 0
  display flex
  flex-direction column
  gap 10px
  overflow hidden
.permission-body-wrap--loading
  min-height 160px
.card-grid--inner
  flex 1
  min-height 0
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  grid-template-rows minmax(0, 1fr)
  align-items stretch
  gap 16px
  overflow hidden
.empty-holder
  width 100%
  flex 1
  min-height 240px
  display flex
  align-items center
  justify-content center
.screen-card
  border-radius 8px
  transition all 0.2s
  border 1px solid #ebeef5
  background #fff
  min-height 0
  &.permission-card
    height 100%
    min-height 0
    align-self stretch
    display flex
    flex-direction column
    overflow hidden
    >>> .el-card__body
      flex 1
      min-height 0
      display flex
      flex-direction column
      overflow hidden
      padding 0 14px 0 14px
  >>> .el-card__body
    padding 0 14px 0 14px
  >>> .el-card__header
    flex-shrink 0
    padding 12px 14px
    border-bottom 1px solid #ebeef5
.card-header
  display flex
  justify-content space-between
  align-items center
.title-with-icon
  display flex
  align-items center
  gap 8px
  .main-icon
    font-size 16px
    font-weight bold
  .screen-name
    font-weight 700
    color #303133
    font-size 14px
.card-content
  flex 1
  min-height 0
  display flex
  flex-direction column
  padding 10px 0 4px 0
  > .url-pick-shell,
  > .sn-pick-shell
    flex 1
    min-height 0
.instance-mini-card
  flex 1
  min-height 0
  display flex
  flex-direction column
  background #fff
  border 1px solid #ebeef5
  border-radius 6px
  padding 10px
.pick-panel
  flex 1
  min-height 0
  border 1px solid #ebeef5
  border-radius 6px
  padding 10px
  overflow auto
  background #fff
  scrollbar-width thin
  scrollbar-color #cbd5e1 transparent
  &::-webkit-scrollbar
    width 6px
  &::-webkit-scrollbar-thumb
    background-color #cbd5e1
    border-radius 6px
  &::-webkit-scrollbar-track
    background transparent
.url-pick-shell
  flex 1
  min-height 0
  display flex
  flex-direction column
  border 1px solid #ebeef5
  border-radius 6px
  padding 10px 10px 6px
  background #fff
  overflow hidden
.url-pick-scroll
  flex 1
  min-height 0
  overflow auto
  scrollbar-width thin
  scrollbar-color #cbd5e1 transparent
  &::-webkit-scrollbar
    width 6px
  &::-webkit-scrollbar-thumb
    background-color #cbd5e1
    border-radius 6px
  &::-webkit-scrollbar-track
    background transparent
.sn-pick-shell
  flex 1
  min-height 0
  display flex
  flex-direction column
  border 1px solid #ebeef5
  border-radius 6px
  padding 10px 10px 6px
  background #fff
  overflow hidden
.sn-pick-scroll
  flex 1
  min-height 0
  overflow auto
  scrollbar-width thin
  scrollbar-color #cbd5e1 transparent
  &::-webkit-scrollbar
    width 6px
  &::-webkit-scrollbar-thumb
    background-color #cbd5e1
    border-radius 6px
  &::-webkit-scrollbar-track
    background transparent
.sn-empty-wrap
  margin-top auto
  flex-shrink 0
  padding 8px 4px 4px
  border-top 1px dashed #e2e8f0
  >>> .el-empty
    padding 12px 0 4px
.pick-panel--no-admin,
.url-pick-shell.pick-panel--no-admin,
.sn-pick-shell.pick-panel--no-admin
  opacity 0.72
  cursor default
  .option-row.clickable
    cursor not-allowed
.sn-panel
  border none
  box-shadow none
  padding 0
  &.sn-pick-shell
    border none
    padding 0
    .sn-pick-scroll
      padding 0
.compact-panel
  min-height 0
.option-list
  display flex
  flex-direction column
  gap 8px
.sn-option-list
  display flex
  flex-direction column
  gap 8px
  .option-title
    word-break break-all
    white-space normal
    line-height 1.4
.option-row
  border 1px solid #e2e8f0
  border-radius 6px
  background #fff
  padding 8px 10px
  transition all .2s ease
  &:hover
    border-color #cbd5e1
    background #f8fafc
.clickable
  cursor pointer
.option-row.active
  border-color #60a5fa
  background #eff6ff
.option-row-main
  display flex
  align-items center
  justify-content space-between
.option-title
  font-weight 600
  color #334155
  line-height 1.35
.option-sub
  margin-top 2px
  font-size 12px
  color #94a3b8
  line-height 1.3
  white-space nowrap
  overflow hidden
  text-overflow ellipsis
  word-break break-all
.group-title
  font-size 13px
  font-weight 600
  color #303133
  margin-bottom 6px
.sub-group
  padding 8px 0
  border-bottom 1px dashed #e2e8f0
  &:last-of-type
    border-bottom none
.sub-group-title
  font-size 12px
  color #909399
  margin-bottom 6px
.homework-search-wrap
  margin-bottom 8px
.homework-list
  overflow visible
.screen-url-option
  display flex
  align-items center
  gap 6px
  i
    margin-top 2px
@media (max-width: 1366px)
  .card-grid--inner
    grid-template-columns 1fr
    grid-template-rows minmax(0, 1fr) minmax(0, 1fr)
    min-height 0
.homework-pagination-wrap
  display flex
  justify-content center
  align-items center
  flex-wrap wrap
  gap 6px
  margin-top auto
  padding 10px 4px 4px
  border-top 1px dashed #e2e8f0
  flex-shrink 0
  background #fff
  >>> .el-pagination
    padding 0
    background transparent
    flex-wrap wrap
    justify-content center
    row-gap 6px
    button
      background transparent
    .el-pager li
      background transparent
      border-radius 4px
      min-width 24px
      height 24px
      line-height 24px
      &.active
        background-color #14b8a6
        color #fff
</style>

